import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Button } from '../ui/button';
import { Save, Download, Trash2, Loader2, Layers, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { loadSlots, saveSlots, clearSlots } from './scenarioSessionStorage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SLOTS = ['A', 'B', 'C'];

const fmtMoney = (v) => (v === null || v === undefined ? '—' : `$${Math.round(v).toLocaleString()}`);
const fmtPct = (v, d = 2) => (v === null || v === undefined ? '—' : `${v.toFixed(d)}%`);
const fmtRatio = (v) => (v === null || v === undefined ? '—' : v.toFixed(2));

const COMPARE_ROWS = [
  { key: 'purchase_price',    i18n: 'scenario.purchasePrice',    from: 'scenario', fmt: fmtMoney },
  { key: 'down_payment_percent', i18n: 'scenario.downPayment',   from: 'scenario', fmt: (v) => fmtPct(v, 1) },
  { key: 'monthly_mortgage',  i18n: 'financialImpact.monthlyMortgage', from: 'metrics', fmt: fmtMoney },
  { key: 'monthly_cash_flow', i18n: 'financialImpact.monthlyCashFlow', from: 'metrics', fmt: fmtMoney },
  { key: 'noi',               i18n: 'financialImpact.noi',       from: 'metrics', fmt: fmtMoney },
  { key: 'cap_rate',          i18n: 'financialImpact.capRate',   from: 'metrics', fmt: fmtPct },
  { key: 'dscr',              i18n: 'financialImpact.dscr',      from: 'metrics', fmt: fmtRatio },
  { key: 'roi',               i18n: 'financialImpact.roi',       from: 'metrics', fmt: fmtPct },
  { key: 'cash_on_cash',      i18n: 'financialImpact.cashOnCash', from: 'metrics', fmt: fmtPct },
];

const ScenarioSlots = ({ userId, scenario, originalDeal, originalMetrics, currentMetrics, onLoadScenario }) => {
  const { t } = useTranslation();
  const [slots, setSlotsState] = useState({ A: null, B: null, C: null });
  const [comparing, setComparing] = useState(false);
  const [comparisonMetrics, setComparisonMetrics] = useState({}); // { A: metrics, B: metrics, C: metrics }
  const [confirmClearAll, setConfirmClearAll] = useState(false);

  // Hydrate from sessionStorage
  useEffect(() => {
    if (!userId) return;
    setSlotsState(loadSlots(userId));
  }, [userId]);

  const persist = useCallback((next) => {
    setSlotsState(next);
    saveSlots(userId, next);
  }, [userId]);

  const saveAs = (slot) => {
    const snapshot = {
      scenario: { ...scenario },
      savedAt: new Date().toISOString(),
    };
    persist({ ...slots, [slot]: snapshot });
    toast.success(t('slots.savedToast', { slot }));
  };

  const load = (slot) => {
    const s = slots[slot];
    if (!s) return;
    onLoadScenario({ ...s.scenario });
    toast.success(t('slots.loadedToast', { slot }));
  };

  const del = (slot) => {
    persist({ ...slots, [slot]: null });
    setComparisonMetrics((m) => { const n = { ...m }; delete n[slot]; return n; });
    toast.success(t('slots.deletedToast', { slot }));
  };

  const clearAll = () => {
    clearSlots(userId);
    setSlotsState({ A: null, B: null, C: null });
    setComparisonMetrics({});
    setConfirmClearAll(false);
    toast.success(t('slots.clearedAllToast'));
  };

  const hasAnySlot = SLOTS.some((s) => slots[s]);

  const compare = useCallback(async () => {
    if (!hasAnySlot) return;
    setComparing(true);
    try {
      const jobs = SLOTS.filter((s) => slots[s]).map((s) =>
        axios.post(
          `${BACKEND_URL}/api/scenario/calculate`,
          { scenario: slots[s].scenario },
          { withCredentials: true }
        ).then((r) => [s, r.data.metrics]).catch(() => [s, null])
      );
      const settled = await Promise.all(jobs);
      const next = {};
      for (const [s, m] of settled) next[s] = m;
      setComparisonMetrics(next);
    } finally {
      setComparing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slots, hasAnySlot]);

  // columns for compare table: Original + Current + saved slots
  const columns = useMemo(() => {
    const cols = [
      { id: 'original', label: t('scenario.originalDeal'), scenario: originalDeal, metrics: originalMetrics },
      { id: 'current',  label: t('scenario.currentScenario'), scenario, metrics: currentMetrics },
    ];
    for (const s of SLOTS) {
      if (slots[s]) cols.push({
        id: s,
        label: t('slots.scenarioLabel', { slot: s }),
        scenario: slots[s].scenario,
        metrics: comparisonMetrics[s] || null,
      });
    }
    return cols;
  }, [originalDeal, originalMetrics, scenario, currentMetrics, slots, comparisonMetrics, t]);

  return (
    <section
      data-testid="scenario-slots-section"
      className="mt-8 bg-white dark:bg-[#131824] rounded-xl p-6 shadow-sm border border-slate-200 dark:border-slate-800"
    >
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <Layers className="w-5 h-5 text-violet-500" />
            <h2 className="font-primary font-bold text-xl text-slate-900 dark:text-slate-100">
              {t('slots.title')}
            </h2>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{t('slots.subtitle')}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            data-testid="slots-compare-btn"
            onClick={compare}
            disabled={!hasAnySlot || comparing}
            size="sm"
            variant="outline"
            className="border-violet-300 dark:border-violet-800 text-violet-700 dark:text-violet-300"
          >
            {comparing ? (<><Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />{t('slots.comparing')}</>)
                       : (<><RefreshCw className="w-3.5 h-3.5 mr-2" />{t('slots.compare')}</>)}
          </Button>
          {hasAnySlot && !confirmClearAll && (
            <Button
              data-testid="slots-clear-all-btn"
              onClick={() => setConfirmClearAll(true)}
              size="sm"
              variant="ghost"
              className="text-slate-500 dark:text-slate-400"
            >
              <Trash2 className="w-3.5 h-3.5 mr-2" />{t('slots.clearAll')}
            </Button>
          )}
          {confirmClearAll && (
            <>
              <Button
                data-testid="slots-clear-all-confirm"
                onClick={clearAll}
                size="sm"
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {t('slots.confirmClearAll')}
              </Button>
              <Button
                data-testid="slots-clear-all-cancel"
                onClick={() => setConfirmClearAll(false)}
                size="sm"
                variant="ghost"
              >
                {t('common.cancel')}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Slot cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        {SLOTS.map((s) => (
          <div
            key={s}
            data-testid={`slot-card-${s}`}
            className={`rounded-xl border p-4 flex flex-col gap-2 ${
              slots[s]
                ? 'border-violet-200 dark:border-violet-800 bg-violet-50/50 dark:bg-violet-900/10'
                : 'border-dashed border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-[#0F1419]'
            }`}
          >
            <div className="flex items-center justify-between">
              <p className="font-primary font-bold text-slate-900 dark:text-slate-100">
                {t('slots.scenarioLabel', { slot: s })}
              </p>
              {slots[s] && (
                <button
                  type="button"
                  data-testid={`slot-delete-${s}`}
                  onClick={() => del(s)}
                  className="text-slate-400 hover:text-red-500"
                  aria-label={t('slots.delete')}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            {slots[s] ? (
              <>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {fmtMoney(slots[s].scenario.purchase_price)} · {fmtPct(slots[s].scenario.down_payment_percent, 1)} DP · {fmtPct(slots[s].scenario.interest_rate, 2)}
                </p>
                <div className="flex gap-2 mt-1">
                  <Button
                    data-testid={`slot-load-${s}`}
                    onClick={() => load(s)}
                    size="sm"
                    variant="outline"
                    className="flex-1 h-8 text-xs"
                  >
                    <Download className="w-3 h-3 mr-1" />{t('slots.load')}
                  </Button>
                  <Button
                    data-testid={`slot-update-${s}`}
                    onClick={() => saveAs(s)}
                    size="sm"
                    className="flex-1 h-8 text-xs bg-violet-600 hover:bg-violet-700 text-white"
                  >
                    <Save className="w-3 h-3 mr-1" />{t('slots.update')}
                  </Button>
                </div>
              </>
            ) : (
              <>
                <p className="text-xs text-slate-500 dark:text-slate-400 flex-1">
                  {t('slots.emptyHint')}
                </p>
                <Button
                  data-testid={`slot-save-${s}`}
                  onClick={() => saveAs(s)}
                  size="sm"
                  className="h-8 text-xs bg-violet-600 hover:bg-violet-700 text-white"
                >
                  <Save className="w-3 h-3 mr-1" />{t('slots.saveCurrentAs', { slot: s })}
                </Button>
              </>
            )}
          </div>
        ))}
      </div>

      {/* Compare view: table (md+) / stacked cards (mobile). */}
      {hasAnySlot && (
        <>
          <p className="text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2">
            {t('slots.comparisonTitle')}
          </p>

          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table data-testid="slots-compare-table" className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-800">
                  <th className="text-left py-2 text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400">{t('financialImpact.metric')}</th>
                  {columns.map((c) => (
                    <th key={c.id} data-testid={`slots-col-${c.id}`}
                        className="text-left py-2 px-2 text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400">
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {COMPARE_ROWS.map((r) => (
                  <tr key={r.key}>
                    <td className="py-2 text-slate-700 dark:text-slate-300">{t(r.i18n)}</td>
                    {columns.map((c) => {
                      const src = r.from === 'scenario' ? c.scenario : c.metrics;
                      const val = src ? src[r.key] : null;
                      return (
                        <td key={c.id} className="py-2 px-2 font-mono tabular-nums text-slate-900 dark:text-slate-100">
                          {r.fmt(val)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile stacked */}
          <div className="md:hidden space-y-3">
            {columns.map((c) => (
              <div key={c.id} data-testid={`slots-mobile-col-${c.id}`}
                   className="rounded-lg border border-slate-200 dark:border-slate-800 p-3">
                <p className="font-primary font-bold text-slate-900 dark:text-slate-100 mb-2">{c.label}</p>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  {COMPARE_ROWS.map((r) => {
                    const src = r.from === 'scenario' ? c.scenario : c.metrics;
                    const val = src ? src[r.key] : null;
                    return (
                      <React.Fragment key={r.key}>
                        <span className="text-slate-500 dark:text-slate-400">{t(r.i18n)}</span>
                        <span className="font-mono tabular-nums text-slate-900 dark:text-slate-100">{r.fmt(val)}</span>
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </section>
  );
};

export default ScenarioSlots;
